import { useCallback, useEffect, useState } from "react";
import { Copy, MessageCircle, Wallet, Users, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import KycForm from "@/components/account/KycForm";
import WithdrawPanel from "@/components/account/WithdrawPanel";
import Leaderboard from "@/components/account/Leaderboard";
import WelcomeKit from "@/components/account/WelcomeKit";
import { LinkOpens, LastMonthReport } from "@/components/account/AgentInsights";
import { useCustomer } from "@/lib/auth";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function ReferEarn() {
  const { user } = useCustomer();
  const [w, setW] = useState(null);
  const load = useCallback(() => api.get("/customer/wallet").then((r) => setW(r.data)).catch(() => toast.error("Could not load wallet")), []);
  useEffect(() => { load(); }, [load]);
  if (!w) return <p className="text-sm text-ink/50" data-testid="wallet-loading">Loading your wallet…</p>;

  const link = `${window.location.origin}/?ref=${w.referral_code}`;
  const shareMsg = `Planning Puri? Book bikes, cabs & Odisha tours with Random Rider — use my link: ${link}`;
  const copy = async () => { await navigator.clipboard.writeText(link); toast.success("Referral link copied"); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="refer-earn">
      <div className="lg:col-span-7 space-y-6 min-w-0">
        <div className="bg-sand border border-ink/10 text-ink rounded-3xl p-7 relative overflow-hidden">
          <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-terra/30 blur-3xl" />
          <p className="font-mono text-[11px] uppercase tracking-widest text-terra">Refer & earn 10%</p>
          <h2 className="font-serif text-3xl font-bold mt-2 leading-tight">Share Puri. Earn on every confirmed booking.</h2>
          <p className="text-sm text-ink/70 mt-3 leading-relaxed">Anyone who books a ride, cab or tour through your link earns you 10% of the trip amount — credited to your wallet the moment we confirm their booking.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            <code className="flex-1 min-w-0 bg-ink/5 border border-ink/15 rounded-xl px-4 py-3 text-sm truncate" data-testid="referral-link">{link}</code>
            <button onClick={copy} data-testid="referral-copy" className="inline-flex items-center justify-center gap-1.5 bg-ink text-white text-sm font-semibold px-4 py-3 rounded-xl hover:bg-gold transition-colors"><Copy size={14} /> Copy</button>
            <a href={`https://wa.me/?text=${encodeURIComponent(shareMsg)}`} target="_blank" rel="noreferrer" data-testid="referral-whatsapp" className="inline-flex items-center justify-center gap-1.5 bg-[#25D366] text-white text-sm font-semibold px-4 py-3 rounded-xl hover:brightness-110"><MessageCircle size={14} /> Share</a>
          </div>
          <p className="mt-3 text-xs text-ink/50">Your agent code: <span className="font-mono text-terra" data-testid="referral-code">{w.referral_code}</span></p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[["Wallet balance", inr(w.balance), Wallet, "wallet-balance"], ["Referred trips", `${w.confirmed_count}/${w.referred_count}`, Users, "wallet-referred"], ["Total earned", inr(w.total_earned), BadgeCheck, "wallet-earned"]].map(([l, v, I, id]) => (
            <div key={l} className="bg-white border border-ink/10 rounded-2xl p-4 min-w-0" data-testid={id}>
              <I size={16} className="text-terra" />
              <p className="font-serif text-xl sm:text-2xl font-bold mt-2 truncate">{v}</p>
              <p className="text-[11px] text-ink/50 uppercase tracking-wider">{l}</p>
            </div>
          ))}
        </div>

        {w.clicks && <LinkOpens clicks={w.clicks} />}
        <WelcomeKit />

        <div className="bg-white border border-ink/10 rounded-3xl p-6">
          <h3 className="font-serif text-xl font-bold">Earnings</h3>
          {w.commissions.length === 0 ? <p className="text-sm text-ink/50 mt-2" data-testid="commissions-empty">No commissions yet — share your link to get started.</p> : (
            <ul className="mt-3 divide-y divide-ink/10" data-testid="commissions-list">
              {w.commissions.map((c) => (
                <li key={c.id} className="py-3 flex items-center justify-between gap-3 text-sm">
                  <div><p className="font-semibold">{c.service || "Booking"} <span className="text-ink/40 font-normal">· {c.enquiry_name}</span></p><p className="text-xs text-ink/50">{new Date(c.created_at).toLocaleDateString("en-IN")} · 10% of {inr(c.base_amount)}</p></div>
                  <span className={`font-semibold ${c.status === "credited" ? "text-emerald-700" : "text-ink/40 line-through"}`}>{inr(c.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="lg:col-span-5 space-y-6">
        <WithdrawPanel wallet={w} onChange={load} />
        <LastMonthReport />
        <KycForm kyc={w.kyc} phone={user?.phone?.replace(/^\+91/, "")} onChange={load} />
        <Leaderboard />
      </div>
    </div>
  );
}
